import { Camera, ChevronDown, Circle, Megaphone, Mic, PenLine, Video } from "lucide-react";
import { Popover } from "radix-ui";
import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import { Sheet } from "@/components/ui/dialog";
import { Toast, type ToastMessage } from "@/components/ui/toast";

import { useImprovementsAvailability } from "./api";
import { FichaMejora } from "./FichaMejora";
import { puedeGrabar, puedeGrabarPantalla, reloj, useGrabadora } from "./grabadora";
import { useReporteRapido } from "./ReporteRapido";

/**
 * **«Reportar mejora» en la barra superior** (M13; menú desde el 20-oct, 0182; para todos): un solo
 * botón con cuatro opciones, todas con el consecutivo `M-0001` y la ruta puesta por el sistema.
 *
 * ```
 * Grabar video          el aviso de privacidad, pantalla + voz con su contador y «Detener» (abajo a la
 *                       derecha, fuera del ERP, para alcanzarlo con el Cotizador a pantalla completa)
 * Nota de voz           la misma grabadora, solo la voz
 * Captura de pantalla   la pantalla tal como se ve, y una nota (ReporteRapido)
 * Corrección puntual    qué está mal y, si quiere, el elemento señalado (ReporteRapido)
 * ```
 *
 * Video y voz usan la grabadora a prueba de cortes (H6): cada trozo va al cajón del navegador y, si al
 * abrir quedó una grabación ahí (se recargó o se cerró grabando), se ofrece guardarla. Sin dónde
 * guardarlas (sin R2 en producción) esas dos salen deshabilitadas con el motivo; captura y corrección
 * van a la base y funcionan siempre.
 */
export function ReportarMejora() {
  const disponible = useImprovementsAvailability().data;
  const max = disponible?.max_seconds ?? 600;
  const { estado, empezar, detener, descartar } = useGrabadora(max);
  const location = useLocation();
  const [aviso, setAviso] = useState(false);
  const [ficha, setFicha] = useState(false);
  const [gracias, setGracias] = useState<ToastMessage | null>(null);
  const [ofrecida, setOfrecida] = useState(false);

  const [menu, setMenu] = useState(false);
  const rapido = useReporteRapido((r) => setGracias({ id: Date.now(), text: `${r.code} guardada. Gracias por contarla.` }));

  const grabable = Boolean(disponible?.enabled) && puedeGrabar();
  const pantalla = puedeGrabarPantalla();
  const pendiente = estado.fase === "grabada";
  const motivo = !disponible?.enabled ? "Falta configurar dónde guardar las grabaciones" : !puedeGrabar() ? "Este navegador no deja grabar" : undefined;

  const recuperada = estado.fase === "grabada" ? estado.recuperada : undefined;

  function arrancar(soloVoz: boolean) {
    setAviso(false);
    setFicha(true);
    void empezar(soloVoz, `${location.pathname}${location.search}`);
  }

  function abrirFicha() {
    setOfrecida(true);
    setFicha(true);
  }

  return (
    <>
      {pendiente ? (
        <button type="button" aria-label="Mejora sin guardar" onClick={abrirFicha} className={BOTON}>
          <Video aria-hidden className="size-4" />
          <span aria-hidden className="hidden sm:inline">Mejora sin guardar</span>
        </button>
      ) : (
        <Popover.Root open={menu} onOpenChange={setMenu}>
          <Popover.Trigger aria-label="Reportar mejora" disabled={estado.fase === "grabando"} className={BOTON}>
            <Megaphone aria-hidden className="size-4" />
            <span aria-hidden className="hidden sm:inline">Reportar mejora</span>
            <ChevronDown aria-hidden className="size-3.5 text-ink-3" />
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content data-sin-captura="" role="menu" aria-label="Reportar mejora" align="end" sideOffset={6}
              className="z-50 grid w-72 gap-1 rounded-xl border border-line bg-surface p-1.5 text-ink shadow-card">
              <Opcion icono={<Video aria-hidden />} titulo="Grabar video"
                detalle={grabable && !pantalla ? "Este navegador no deja grabar la pantalla: usa Nota de voz" : motivo ?? "Tu pantalla y tu voz"}
                disabled={!grabable || !pantalla} onClick={() => { setMenu(false); setAviso(true); }} />
              <Opcion icono={<Mic aria-hidden />} titulo="Nota de voz" detalle={motivo ?? "Solo tu voz"}
                disabled={!grabable} onClick={() => { setMenu(false); arrancar(true); }} />
              <Opcion icono={<Camera aria-hidden />} titulo="Captura de pantalla" detalle="Una foto de lo que ves ahora"
                onClick={() => { setMenu(false); rapido.iniciarCaptura(); }} />
              <Opcion icono={<PenLine aria-hidden />} titulo="Corrección puntual" detalle="Escribe o señala lo que está mal"
                onClick={() => { setMenu(false); rapido.iniciarCorreccion(); }} />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}
      {rapido.hoja}

      <Sheet open={aviso} onOpenChange={setAviso} title="Grabar video" description="Se grabará tu pantalla y tu voz; solo la verá el dueño del ERP.">
        <div className="grid gap-4 text-sm text-ink-2">
          <p>Muestra lo que encontraste y cuéntalo en voz alta. Hasta {Math.round(max / 60)} minutos: al llegar, se detiene sola.</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => arrancar(false)}>Empezar a grabar</Button>
            <Button type="button" variant="secondary" onClick={() => setAviso(false)}>Cancelar</Button>
          </div>
        </div>
      </Sheet>

      {estado.fase === "grabando" ? createPortal(
        <div role="status" aria-label="Grabación en curso" className="fixed right-4 bottom-4 z-50 flex items-center gap-3 rounded-lg bg-surface px-3.5 py-2.5 text-sm text-ink shadow-soft">
          <Circle aria-hidden className="size-3 fill-bad text-bad" />
          <span className="tabular-nums">Grabando {reloj(estado.segundos)} / {reloj(max)}</span>
          {estado.conVoz ? null : <span className="text-warn">sin micrófono</span>}
          <Button type="button" variant="danger" className="min-h-9" onClick={() => detener()}>Detener</Button>
        </div>,
        document.body,
      ) : null}

      {estado.fase === "error" ? createPortal(
        <div role="alert" className="fixed right-4 bottom-4 z-50 flex max-w-sm items-center gap-3 rounded-lg bg-surface px-3.5 py-2.5 text-sm text-ink shadow-soft">
          <span>{estado.mensaje}</span>
          <button type="button" className="font-semibold underline" onClick={descartar}>Cerrar</button>
        </div>,
        document.body,
      ) : null}

      {recuperada && !ofrecida && !ficha && estado.fase === "grabada" ? createPortal(
        <div role="status" aria-label="Grabación sin guardar" className="fixed right-4 bottom-4 z-50 flex max-w-sm flex-wrap items-center gap-3 rounded-lg bg-surface px-3.5 py-2.5 text-sm text-ink shadow-soft">
          <span className="mr-auto">
            {recuperada === "cortada"
              ? `Tienes una grabación sin terminar de ${reloj(estado.segundos)}: se guardó hasta donde llegó.`
              : `Tienes una grabación sin guardar de ${reloj(estado.segundos)}.`}
          </span>
          <Button type="button" className="min-h-9" onClick={abrirFicha}>Guardarla</Button>
          <Button type="button" variant="secondary" className="min-h-9" onClick={() => setOfrecida(true)}>Después</Button>
        </div>,
        document.body,
      ) : null}

      {estado.fase === "grabada" ? (
        <Sheet open={ficha} onOpenChange={setFicha} title="Guardar la mejora"
          description={estado.porTope
            ? `Llegaste a los ${Math.round(max / 60)} minutos: la grabación se detuvo sola y quedó lista.`
            : recuperada === "cortada"
              ? "La página se cerró mientras grababas: esto es lo que alcanzó a quedar. Cuéntale al administrador qué es."
              : "La grabación quedó lista. Cuéntale al administrador qué es."}>
          <FichaMejora
            archivo={estado.archivo}
            segundos={estado.segundos}
            ruta={estado.ruta}
            maxBytes={disponible?.max_bytes ?? 95 * 1024 * 1024}
            onGuardada={(codigo) => { descartar(); setFicha(false); setGracias({ id: Date.now(), text: `${codigo ?? "Mejora"} guardada. Gracias por contarla.` }); }}
            onDescartar={() => { descartar(); setFicha(false); }}
          />
        </Sheet>
      ) : null}

      {gracias ? <Toast message={gracias} onDone={() => setGracias(null)} /> : null}
    </>
  );
}

const BOTON = "inline-flex min-h-10 items-center gap-2 rounded-lg border border-line px-3 text-sm font-semibold text-ink transition-colors hover:bg-surface-2 disabled:opacity-50";

function Opcion({ icono, titulo, detalle, disabled, onClick }: { icono: ReactNode; titulo: string; detalle: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" role="menuitem" disabled={disabled} onClick={onClick}
      className="flex min-h-11 items-start gap-3 rounded-lg px-3 py-2 text-left text-ink hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:mt-0.5 [&_svg]:size-4 [&_svg]:shrink-0">
      {icono}
      <span className="grid">
        <span className="text-sm font-semibold">{titulo}</span>
        <span className="text-xs text-ink-3">{detalle}</span>
      </span>
    </button>
  );
}
